import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Megaphone } from "lucide-react";

import FeedbackWidget from "../components/Common/FeedbackWidget";

/**
 * The page masthead: icon + title + subtitle, optional actions, optional
 * back-link, and the per-page feedback entry point.
 *
 * Replaces PageTitle, whose breadcrumb was two non-clickable strings
 * restating the sidebar's group + item — no information the nav didn't
 * already show, and drifted out of sync with it over time ("Time Tracking"
 * vs "Tid", "Fakturor och Offerter" vs "Fakturering och offerter").
 *
 * The shape here is lifted from the hand-rolled headers on Tillgångar and
 * Bankhändelser; this exists so those stop being copy-pasted inline styles.
 * PageTitle itself is gone — its last consumers were two unrouted JOBICK
 * template pages, deleted alongside it.
 */

const slugify = (value) =>
    String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

const PageHeader = ({
    icon: Icon,
    title,
    subtitle,
    actions,
    // { to, label, onClick? } — for detail pages reached from a list. A breadcrumb never
    // did this job (it wasn't clickable); this actually navigates.
    backTo,
    children,
    hideFeedback = false,
    feedbackSource,
}) => {
    const [showFeedback, setShowFeedback] = useState(false);

    // Same derivation PageTitle used, so feedback attribution stays continuous
    // across the migration instead of every page reverting to "page".
    const source = feedbackSource || slugify(title) || "page";

    return (
        <div className="page-masthead">
            {backTo && (
                <Link to={backTo.to} onClick={backTo.onClick} className="page-masthead__back">
                    <ArrowLeft size={14} strokeWidth={2} />
                    {backTo.label}
                </Link>
            )}

            <div className="page-masthead__row">
                <div className="page-masthead__identity">
                    {Icon && (
                        <span className="page-masthead__icon">
                            <Icon size={28} strokeWidth={1.75} />
                        </span>
                    )}
                    <div className="page-masthead__text">
                        <h1 className="page-masthead__title">{title}</h1>
                        {subtitle && <p className="page-masthead__subtitle">{subtitle}</p>}
                        {/* Extra lines under the subtitle — e.g. Bankhändelser'
                            sync status. Kept as a slot so pages don't have to
                            fork the component for one more line of meta. */}
                        {children}
                    </div>
                </div>

                <div className="page-masthead__actions">
                    {actions}
                    {/* Feedback is a standing utility, not one of this page's
                        actions — it gets its own group so a primary CTA and a
                        quiet link don't read as one button cluster. */}
                    {!hideFeedback && (
                        <button
                            type="button"
                            onClick={() => setShowFeedback(true)}
                            className={`page-masthead__feedback${actions ? ' is-separated' : ''}`}
                        >
                            <Megaphone size={13} strokeWidth={1.75} />
                            Feedback
                        </button>
                    )}
                </div>
            </div>

            {!hideFeedback && (
                <FeedbackWidget
                    show={showFeedback}
                    onHide={() => setShowFeedback(false)}
                    source={source}
                />
            )}
        </div>
    );
};

export default PageHeader;
